import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, Wand2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * AIWandButton — small "improve with AI" button.
 *
 * Usage:
 *   <AIWandButton
 *     value={desc}
 *     onImproved={(text) => setDesc(text)}
 *     endpoint="/ai/write-invoice-description"
 *     payloadBuilder={(text) => ({ notes: text })}
 *     resultKey="description"
 *   />
 */
export function AIWandButton({
  value,
  onImproved,
  endpoint = '/ai/write-invoice-description',
  payloadBuilder = (v) => ({ notes: v }),
  resultKey = 'description',
  disabled = false,
  size = 'sm',
  variant = 'outline',
  label = 'Mejorar con IA',
  compact = false,
  testId = 'ai-wand-btn',
}) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const src = (value || '').trim();
    if (!src) return toast.error('Escriba algunas notas primero');
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, payloadBuilder(src));
      const improved = data?.[resultKey];
      if (!improved) {
        toast.error('AI no devolvió un resultado válido');
        return;
      }
      onImproved?.(improved, data);
      toast.success('AI mejoró el texto');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al llamar al AI';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={compact ? 'icon' : size}
      onClick={run}
      disabled={disabled || busy}
      className={compact ? '' : 'gap-1.5'}
      title={label}
      data-testid={testId}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 text-emerald-600" />}
      {!compact && <span className="text-xs">{label}</span>}
    </Button>
  );
}

export default AIWandButton;
